import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import { formatDate, getInitials } from "utils/helpers";
import {
    sectorEvaluationApi,
    registerEvaluationApi,
    lawImplementationEvaluationApi,
    legalAdvisoryEvaluationApi,
    legalAwarenessEvaluationApi,
    legalResearchEvaluationApi,
    internalLegalResearchEvaluationApi,
    legalTrainingEvaluationApi,
    legalTranslationEvaluationApi,
    legislativeDraftingEvaluationApi,
} from "services/directiveEvaluationService";
import { getSectorDirectives } from "services/sectorDirectives";
import { getRegisterDirectives } from "services/registerDirectivesService";
import { getLawImplementations } from "services/lawImplementationService";
import { getLegalAdvisories } from "services/legalAdvisoryService";
import { getLegalAwareness } from "services/legalAwarenessService";
import { getLegalResearchRequests } from "services/legalResearchService";
import { getLegalTrainingProvisions } from "services/legalTrainingProvision";
import { getLegalTranslations } from "services/legalTranslationService";
import { getLegislativeDraftings } from "services/legislativeService";

interface EvaluationReportModalProps {
    bearer: string;
    onClose: () => void;
}

/**
 * A request type that can carry team evaluations.
 * `available` sources have live evaluation endpoints; others are listed in the
 * picker but disabled until their backend evaluation APIs are added — at which
 * point you only need to fill in `loaders`.
 */
interface EvalSource {
    key: string;
    label: string;
    fallback: string;
    available: boolean;
    loaders?: {
        evals: (bearer: string) => Promise<any>;
        pros?: (bearer: string) => Promise<any>;
        reqs?: (bearer: string) => Promise<any>;
    };
}

const SOURCES: EvalSource[] = [
    {
        key: "sector",
        label: "CheckingAccuracyOfSectorDirectives",
        fallback: "Checking Accuracy of Sectors",
        available: true,
        loaders: {
            evals: (b) => sectorEvaluationApi.getEvaluations(b),
            pros: (b) => sectorEvaluationApi.getProsecutors(b),
            reqs: getSectorDirectives,
        },
    },
    {
        key: "register",
        label: "RegisterDirective",
        fallback: "Register Directives",
        available: true,
        loaders: {
            evals: (b) => registerEvaluationApi.getEvaluations(b),
            pros: (b) => registerEvaluationApi.getProsecutors(b),
            reqs: getRegisterDirectives,
        },
    },
    {
        key: "advisory",
        label: "LegalAdvisory",
        fallback: "Legal Advisory",
        available: true,
        loaders: {
            evals: (b) => legalAdvisoryEvaluationApi.getEvaluations(b),
            pros: (b) => legalAdvisoryEvaluationApi.getProsecutors(b),
            reqs: () => getLegalAdvisories(),
        },
    },
    {
        key: "translation",
        label: "LegalTranslation",
        fallback: "Legal Translation",
        available: true,
        loaders: {
            evals: (b) => legalTranslationEvaluationApi.getEvaluations(b),
            pros: (b) => legalTranslationEvaluationApi.getProsecutors(b),
            reqs: () => getLegalTranslations(),
        },
    },
    {
        key: "legislative",
        label: "LegislativeDrafting",
        fallback: "Legislative Drafting",
        available: true,
        loaders: {
            evals: (b) => legislativeDraftingEvaluationApi.getEvaluations(b),
            pros: (b) => legislativeDraftingEvaluationApi.getProsecutors(b),
            reqs: () => getLegislativeDraftings(),
        },
    },
    {
        key: "research",
        label: "ConductLegalResearch",
        fallback: "Conduct Legal Research",
        available: true,
        loaders: {
            evals: (b) => legalResearchEvaluationApi.getEvaluations(b),
            pros: (b) => legalResearchEvaluationApi.getProsecutors(b),
            reqs: (b) => getLegalResearchRequests(b),
        },
    },
    {
        key: "research_internal",
        label: "ConductLegalResearchInternal",
        fallback: "Legal Research (Internal)",
        available: true,
        loaders: {
            evals: (b) => internalLegalResearchEvaluationApi.getEvaluations(b),
            pros: (b) => internalLegalResearchEvaluationApi.getProsecutors(b),
            reqs: (b) => getLegalResearchRequests(b),
        },
    },
    {
        key: "law_implementation",
        label: "LawImplementation",
        fallback: "Law Implementation",
        available: true,
        loaders: {
            evals: (b) => lawImplementationEvaluationApi.getEvaluations(b),
            pros: (b) => lawImplementationEvaluationApi.getProsecutors(b),
            reqs: () => getLawImplementations(),
        },
    },
    {
        key: "awareness",
        label: "LegalAwarenessCreation",
        fallback: "Legal Awareness",
        available: true,
        loaders: {
            evals: (b) => legalAwarenessEvaluationApi.getEvaluations(b),
            pros: (b) => legalAwarenessEvaluationApi.getProsecutors(b),
            reqs: () => getLegalAwareness(),
        },
    },
    {
        key: "training",
        label: "LegalTrainingProvision",
        fallback: "Legal Training Provision",
        available: true,
        loaders: {
            evals: (b) => legalTrainingEvaluationApi.getEvaluations(b),
            pros: (b) => legalTrainingEvaluationApi.getProsecutors(b),
            reqs: () => getLegalTrainingProvisions(),
        },
    },
];

interface NormalizedEval {
    id: string;
    sourceKey: string;
    sourceLabel: string;
    requestId: string;
    date: string;
    evaluatedBy: string;
    speed: number;
    quality: number;
    load: number;
    overall: number;
    members: { userId: string; name: string }[];
}

const asArray = (res: any): any[] =>
    Array.isArray(res) ? res : res?.results ?? [];

const num = (v: any): number => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
};

const PERIODS: { key: string; label: string; fallback: string; months: number }[] =
    [
        { key: "q", label: "Last3Months", fallback: "Last 3 Months", months: 3 },
        { key: "h", label: "Last6Months", fallback: "Last 6 Months", months: 6 },
        { key: "y", label: "Last1Year", fallback: "Last 1 Year", months: 12 },
        { key: "all", label: "AllTime", fallback: "All Time", months: 0 },
    ];

export default function EvaluationReportModal({
    bearer,
    onClose,
}: EvaluationReportModalProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("q");
    const [evaluations, setEvaluations] = useState<NormalizedEval[]>([]);
    const [requestMap, setRequestMap] = useState<Record<string, any>>({});
    const [selectedTypes, setSelectedTypes] = useState<string[]>(
        SOURCES.filter((s) => s.available).map((s) => s.key)
    );
    const [selectedProsecutor, setSelectedProsecutor] = useState<string>("all");

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const available = SOURCES.filter((s) => s.available && s.loaders);
                const perSource = await Promise.all(
                    available.map(async (s) => {
                        const evalRes = await s
                            .loaders!.evals(bearer)
                            .catch(() => []);
                        const rawEvals = asArray(evalRes);
                        const needFallback = rawEvals.some(
                            (e: any) =>
                                !e.prosecutor_evaluations ||
                                e.prosecutor_evaluations.length === 0
                        );
                        let pros: any[] = [];
                        if (needFallback && s.loaders!.pros) {
                            try {
                                pros = asArray(await s.loaders!.pros(bearer));
                            } catch {
                                pros = [];
                            }
                        }
                        let reqs: any[] = [];
                        if (s.loaders!.reqs) {
                            try {
                                reqs = asArray(await s.loaders!.reqs(bearer));
                            } catch {
                                reqs = [];
                            }
                        }
                        return { source: s, rawEvals, pros, reqs };
                    })
                );

                const allEvals: NormalizedEval[] = [];
                const map: Record<string, any> = {};

                perSource.forEach(({ source, rawEvals, pros, reqs }) => {
                    reqs.forEach((r: any) => {
                        map[`${source.key}:${String(r.id)}`] = {
                            reference:
                                r.request_letter_reference_number ||
                                r.application_number ||
                                "—",
                            organization: r.organization_name || "",
                        };
                    });
                    rawEvals.forEach((e: any) => {
                        const links =
                            e.prosecutor_evaluations &&
                            e.prosecutor_evaluations.length
                                ? e.prosecutor_evaluations
                                : pros.filter(
                                      (p: any) =>
                                          String(p.evaluation) === String(e.id)
                                  );
                        const speed = num(e.work_speed_evaluation);
                        const quality = num(e.work_quality_evaluation);
                        const load = num(e.work_load_evaluation);
                        allEvals.push({
                            id: String(e.id),
                            sourceKey: source.key,
                            sourceLabel: t(source.label) || source.fallback,
                            requestId: String(e.directive_request ?? ""),
                            date: e.evaluation_date || e.cretedAt || "",
                            evaluatedBy: e.evaluated_by || "—",
                            speed,
                            quality,
                            load,
                            overall: Math.round((speed + quality + load) / 3),
                            members: links.map((p: any) => ({
                                userId: String(p.prosecutor_user_id ?? ""),
                                name:
                                    p.prosecutor_name ||
                                    p.prosecutor_user_id ||
                                    "—",
                            })),
                        });
                    });
                });

                if (!active) return;
                setEvaluations(allEvals);
                setRequestMap(map);
            } catch (err) {
                console.error("Failed to load evaluation report:", err);
                showToast(
                    t("FailedToLoadReport") ||
                        "Failed to load evaluation report.",
                    "error"
                );
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bearer]);

    const months = PERIODS.find((p) => p.key === period)?.months ?? 0;

    const inRange = useMemo(() => {
        let list = evaluations.filter((e) =>
            selectedTypes.includes(e.sourceKey)
        );
        if (months !== 0) {
            const cutoff = new Date();
            cutoff.setMonth(cutoff.getMonth() - months);
            list = list.filter((e) => {
                if (!e.date) return false;
                const d = new Date(e.date);
                return !isNaN(d.getTime()) && d >= cutoff;
            });
        }
        return list;
    }, [evaluations, months, selectedTypes]);

    // Per-prosecutor cumulative aggregation across the selected request types
    const prosecutorRows = useMemo(() => {
        const agg: Record<string, any> = {};
        inRange.forEach((e) => {
            e.members.forEach((m) => {
                if (!m.userId) return;
                if (!agg[m.userId])
                    agg[m.userId] = {
                        userId: m.userId,
                        name: m.name,
                        count: 0,
                        speed: 0,
                        quality: 0,
                        load: 0,
                        types: new Set<string>(),
                    };
                const a = agg[m.userId];
                a.count += 1;
                a.speed += e.speed;
                a.quality += e.quality;
                a.load += e.load;
                a.types.add(e.sourceLabel);
                if (m.name && m.name !== "—") a.name = m.name;
            });
        });
        return Object.values(agg)
            .map((a: any) => {
                const speed = a.count ? a.speed / a.count : 0;
                const quality = a.count ? a.quality / a.count : 0;
                const load = a.count ? a.load / a.count : 0;
                return {
                    userId: a.userId,
                    name: a.name,
                    count: a.count,
                    types: Array.from(a.types) as string[],
                    avgSpeed: Math.round(speed),
                    avgQuality: Math.round(quality),
                    avgLoad: Math.round(load),
                    overall: Math.round((speed + quality + load) / 3),
                };
            })
            .sort((a, b) => b.overall - a.overall);
    }, [inRange]);

    // Reset the prosecutor focus if they drop out of the current scope
    // (e.g. after changing period / request types).
    useEffect(() => {
        if (
            selectedProsecutor !== "all" &&
            !prosecutorRows.some((r) => r.userId === selectedProsecutor)
        ) {
            setSelectedProsecutor("all");
        }
    }, [prosecutorRows, selectedProsecutor]);

    const selectedRow =
        selectedProsecutor === "all"
            ? null
            : prosecutorRows.find((r) => r.userId === selectedProsecutor) || null;

    // Evaluations visible after the optional prosecutor filter.
    const visibleEvals = useMemo(
        () =>
            selectedProsecutor === "all"
                ? inRange
                : inRange.filter((e) =>
                      e.members.some((m) => m.userId === selectedProsecutor)
                  ),
        [inRange, selectedProsecutor]
    );

    // For a focused prosecutor: how they scored per request type.
    const byTypeRows = useMemo(() => {
        if (selectedProsecutor === "all") return [];
        const agg: Record<string, any> = {};
        visibleEvals.forEach((e) => {
            if (!agg[e.sourceLabel])
                agg[e.sourceLabel] = {
                    type: e.sourceLabel,
                    count: 0,
                    speed: 0,
                    quality: 0,
                    load: 0,
                };
            const a = agg[e.sourceLabel];
            a.count += 1;
            a.speed += e.speed;
            a.quality += e.quality;
            a.load += e.load;
        });
        return Object.values(agg).map((a: any) => {
            const speed = a.count ? a.speed / a.count : 0;
            const quality = a.count ? a.quality / a.count : 0;
            const load = a.count ? a.load / a.count : 0;
            return {
                type: a.type,
                count: a.count,
                avgSpeed: Math.round(speed),
                avgQuality: Math.round(quality),
                avgLoad: Math.round(load),
                overall: Math.round((speed + quality + load) / 3),
            };
        });
    }, [visibleEvals, selectedProsecutor]);

    const requestRows = useMemo(
        () =>
            visibleEvals
                .slice()
                .sort((a, b) => String(b.date).localeCompare(String(a.date)))
                .map((e) => ({
                    type: e.sourceLabel,
                    reference:
                        requestMap[`${e.sourceKey}:${e.requestId}`]?.reference ||
                        "—",
                    organization:
                        requestMap[`${e.sourceKey}:${e.requestId}`]
                            ?.organization || "",
                    date: e.date,
                    evaluatedBy: e.evaluatedBy,
                    speed: e.speed,
                    quality: e.quality,
                    load: e.load,
                    overall: e.overall,
                    memberCount: e.members.length,
                })),
        [visibleEvals, requestMap]
    );

    const periodLabel =
        PERIODS.find((p) => p.key === period)?.fallback || "Report";

    const overallTeamAvg = prosecutorRows.length
        ? Math.round(
              prosecutorRows.reduce((s, r) => s + r.overall, 0) /
                  prosecutorRows.length
          )
        : 0;

    const toggleType = (key: string) => {
        setSelectedTypes((prev) =>
            prev.includes(key)
                ? prev.filter((k) => k !== key)
                : [...prev, key]
        );
    };

    const exportToExcel = () => {
        const esc = (v: any) =>
            String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
        const typesLabel = SOURCES.filter((s) => selectedTypes.includes(s.key))
            .map((s) => t(s.label) || s.fallback)
            .join(", ");
        const focused = selectedRow !== null;
        const title = focused
            ? `${t("EvaluationSummary") || "Evaluation Summary"}: ${selectedRow!.name} — ${periodLabel}`
            : `${t("TeamEvaluationSummary") || "Team Evaluation Summary"} — ${periodLabel}`;

        // First table: per-prosecutor (all) OR per-type breakdown (focused).
        const firstTitle = focused
            ? t("PerRequestTypeBreakdown") || "Per-Request-Type Breakdown"
            : t("PerProsecutorCumulative") || "Per-Prosecutor (Cumulative)";
        const prosHeader = focused
            ? [
                  t("RequestType") || "Request Type",
                  t("Evaluations") || "Evaluations",
                  t("WorkSpeed") || "Work Speed (%)",
                  t("WorkQuality") || "Work Quality (%)",
                  t("WorkLoad") || "Work Load (%)",
                  t("Overall") || "Overall (%)",
              ]
            : [
                  t("Prosecutor") || "Prosecutor",
                  t("RequestTypes") || "Request Types",
                  t("Evaluations") || "Evaluations",
                  t("WorkSpeed") || "Work Speed (%)",
                  t("WorkQuality") || "Work Quality (%)",
                  t("WorkLoad") || "Work Load (%)",
                  t("Overall") || "Overall (%)",
              ];
        const prosBody = focused
            ? byTypeRows
                  .map(
                      (r) =>
                          `<tr><td>${esc(r.type)}</td><td>${r.count}</td><td>${r.avgSpeed}</td><td>${r.avgQuality}</td><td>${r.avgLoad}</td><td>${r.overall}</td></tr>`
                  )
                  .join("")
            : prosecutorRows
                  .map(
                      (r) =>
                          `<tr><td>${esc(r.name)}</td><td>${esc(
                              r.types.join(", ")
                          )}</td><td>${r.count}</td><td>${r.avgSpeed}</td><td>${r.avgQuality}</td><td>${r.avgLoad}</td><td>${r.overall}</td></tr>`
                  )
                  .join("");

        const reqHeader = [
            t("RequestType") || "Request Type",
            t("Reference") || "Reference",
            t("Organization") || "Organization",
            t("Date") || "Date",
            t("EvaluatedBy") || "Evaluated By",
            t("WorkSpeed") || "Work Speed (%)",
            t("WorkQuality") || "Work Quality (%)",
            t("WorkLoad") || "Work Load (%)",
            t("Overall") || "Overall (%)",
            t("Members") || "Members",
        ];
        const reqBody = requestRows
            .map(
                (r) =>
                    `<tr><td>${esc(r.type)}</td><td>${esc(r.reference)}</td><td>${esc(
                        r.organization
                    )}</td><td>${esc(
                        r.date ? formatDate(r.date) : "—"
                    )}</td><td>${esc(r.evaluatedBy)}</td><td>${r.speed}</td><td>${r.quality}</td><td>${r.load}</td><td>${r.overall}</td><td>${r.memberCount}</td></tr>`
            )
            .join("");

        const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8" /></head>
        <body>
            <h2>${esc(title)}</h2>
            <p><b>${esc(t("RequestTypes") || "Request Types")}:</b> ${esc(typesLabel)}</p>
            <h3>${esc(firstTitle)}</h3>
            <table border="1">
                <thead><tr>${prosHeader.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
                <tbody>${prosBody || `<tr><td colspan="${prosHeader.length}">${esc(t("NoData") || "No data")}</td></tr>`}</tbody>
            </table>
            <br/>
            <h3>${esc(t("PerRequest") || "Per-Request")}</h3>
            <table border="1">
                <thead><tr>${reqHeader.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
                <tbody>${reqBody || `<tr><td colspan="${reqHeader.length}">${esc(t("NoData") || "No data")}</td></tr>`}</tbody>
            </table>
        </body></html>`;

        const blob = new Blob(["﻿", html], {
            type: "application/vnd.ms-excel;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `team-evaluation-summary-${period}-${
            new Date().toISOString().split("T")[0]
        }.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[92vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#9B0505] to-[#6d0303] px-6 py-5 flex items-start justify-between">
                    <div>
                        <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
                            {t("OfficeOfTheAttorneyGeneral") ||
                                "Office of the Attorney General"}
                        </p>
                        <h2 className="text-white text-xl font-bold mt-1">
                            {t("TeamEvaluationSummary") ||
                                "Team Evaluation Summary"}
                        </h2>
                        <p className="text-white/70 text-sm mt-1">
                            {t("CumulativeAcrossRequests") ||
                                "Cumulative performance across request types."}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition"
                        aria-label="Close"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Request-type picker */}
                <div className="px-6 pt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {t("RequestTypes") || "Request Types"}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {SOURCES.map((s) => {
                            const selected = selectedTypes.includes(s.key);
                            const label = t(s.label) || s.fallback;
                            if (!s.available) {
                                return (
                                    <span
                                        key={s.key}
                                        title={
                                            t("EvaluationApiNotAvailable") ||
                                            "Evaluation API not available yet"
                                        }
                                        className="text-sm rounded-full px-3 py-1.5 border border-dashed border-gray-300 text-gray-400 cursor-not-allowed select-none"
                                    >
                                        {label}
                                        <span className="ml-1 text-[10px]">
                                            ({t("Soon") || "soon"})
                                        </span>
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => toggleType(s.key)}
                                    className={`text-sm rounded-full px-3 py-1.5 border transition ${
                                        selected
                                            ? "bg-[#9B0505] text-white border-[#9B0505]"
                                            : "bg-white text-gray-600 border-gray-300 hover:border-[#9B0505]"
                                    }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Controls */}
                <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                        {PERIODS.map((p) => (
                            <button
                                key={p.key}
                                onClick={() => setPeriod(p.key)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                                    period === p.key
                                        ? "bg-white text-[#9B0505] shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {t(p.label) || p.fallback}
                            </button>
                        ))}
                    </div>
                    <select
                        value={selectedProsecutor}
                        onChange={(e) => setSelectedProsecutor(e.target.value)}
                        disabled={loading || prosecutorRows.length === 0}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#9B0505]/30 focus:border-[#9B0505] disabled:opacity-50 max-w-[220px]"
                    >
                        <option value="all">
                            {t("AllProsecutors") || "All Prosecutors"}
                        </option>
                        {prosecutorRows.map((r) => (
                            <option key={r.userId} value={r.userId}>
                                {r.name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={exportToExcel}
                        disabled={loading || inRange.length === 0}
                        className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition disabled:opacity-50"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t("ExportToExcel") || "Export to Excel"}
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto scrollbar-hide space-y-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
                            {LoadingIcon} {t("Loading") || "Loading"}
                        </div>
                    ) : selectedTypes.length === 0 ? (
                        <p className="text-center text-gray-500 py-16">
                            {t("SelectAtLeastOneType") ||
                                "Select at least one request type."}
                        </p>
                    ) : inRange.length === 0 ? (
                        <p className="text-center text-gray-500 py-16">
                            {t("NoEvaluationsInPeriod") ||
                                "No evaluations in the selected period."}
                        </p>
                    ) : (
                        <>
                            {selectedRow ? (
                                /* Focused prosecutor header */
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800 text-amber-400 flex items-center justify-center font-bold text-lg select-none">
                                            {getInitials(selectedRow.name)}
                                        </div>
                                        <div className="flex-grow">
                                            <p className="font-bold text-lg text-gray-800">
                                                {selectedRow.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {selectedRow.count}{" "}
                                                {t("Evaluations") ||
                                                    "evaluations"}{" "}
                                                · {selectedRow.types.join(", ")}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-3xl font-bold text-[#9B0505] leading-none">
                                                {selectedRow.overall}%
                                            </p>
                                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                                                {t("Overall") || "Overall"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            {
                                                l: t("WorkSpeed") || "Speed",
                                                v: selectedRow.avgSpeed,
                                            },
                                            {
                                                l: t("WorkQuality") || "Quality",
                                                v: selectedRow.avgQuality,
                                            },
                                            {
                                                l: t("WorkLoad") || "Load",
                                                v: selectedRow.avgLoad,
                                            },
                                        ].map((m) => (
                                            <div
                                                key={m.l}
                                                className="bg-gray-50 rounded-lg p-3 text-center"
                                            >
                                                <p className="text-xl font-bold text-gray-800">
                                                    {m.v}%
                                                </p>
                                                <p className="text-[11px] text-gray-500">
                                                    {m.l}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                /* Team summary stats */
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-800">
                                            {inRange.length}
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t("RequestsEvaluated") ||
                                                "Requests evaluated"}
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-800">
                                            {prosecutorRows.length}
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t("ProsecutorsCovered") ||
                                                "Prosecutors covered"}
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-bold text-[#9B0505]">
                                            {overallTeamAvg}%
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t("AverageScore") ||
                                                "Average score"}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* First table: per-type breakdown (focused) or per-prosecutor (all) */}
                            {selectedRow ? (
                                <div>
                                    <h3 className="font-semibold text-gray-800 mb-2">
                                        {t("PerRequestTypeBreakdown") ||
                                            "Per-Request-Type Breakdown"}
                                    </h3>
                                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-600">
                                                <tr>
                                                    <th className="text-left px-4 py-2 font-medium">
                                                        {t("RequestType") ||
                                                            "Request Type"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("Evaluations") ||
                                                            "Evaluations"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkSpeed") ||
                                                            "Speed"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkQuality") ||
                                                            "Quality"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkLoad") || "Load"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("Overall") ||
                                                            "Overall"}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {byTypeRows.map((r) => (
                                                    <tr
                                                        key={r.type}
                                                        className="border-t border-gray-100"
                                                    >
                                                        <td className="px-4 py-2 text-gray-800">
                                                            {r.type}
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.count}
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgSpeed}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgQuality}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgLoad}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 font-semibold text-[#9B0505]">
                                                            {r.overall}%
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-semibold text-gray-800">
                                            {t("PerProsecutorCumulative") ||
                                                "Per-Prosecutor (Cumulative)"}
                                        </h3>
                                        <span className="text-xs text-gray-400">
                                            {t("ClickRowForDetail") ||
                                                "Click a row for details"}
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-600">
                                                <tr>
                                                    <th className="text-left px-4 py-2 font-medium">
                                                        {t("Prosecutor") ||
                                                            "Prosecutor"}
                                                    </th>
                                                    <th className="text-left px-3 py-2 font-medium">
                                                        {t("RequestTypes") ||
                                                            "Request Types"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("Evaluations") ||
                                                            "Evaluations"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkSpeed") ||
                                                            "Speed"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkQuality") ||
                                                            "Quality"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("WorkLoad") || "Load"}
                                                    </th>
                                                    <th className="text-center px-3 py-2 font-medium">
                                                        {t("Overall") ||
                                                            "Overall"}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {prosecutorRows.map((r) => (
                                                    <tr
                                                        key={r.userId}
                                                        onClick={() =>
                                                            setSelectedProsecutor(
                                                                r.userId
                                                            )
                                                        }
                                                        className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                                                    >
                                                        <td className="px-4 py-2 text-gray-800">
                                                            {r.name}
                                                        </td>
                                                        <td className="px-3 py-2 text-gray-500 text-xs">
                                                            {r.types.join(", ")}
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.count}
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgSpeed}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgQuality}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 text-gray-600">
                                                            {r.avgLoad}%
                                                        </td>
                                                        <td className="text-center px-3 py-2 font-semibold text-[#9B0505]">
                                                            {r.overall}%
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Per-request table */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">
                                    {t("PerRequest") || "Per-Request"}
                                </h3>
                                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-gray-600">
                                            <tr>
                                                <th className="text-left px-3 py-2 font-medium">
                                                    {t("RequestType") ||
                                                        "Type"}
                                                </th>
                                                <th className="text-left px-4 py-2 font-medium">
                                                    {t("Reference") ||
                                                        "Reference"}
                                                </th>
                                                <th className="text-left px-3 py-2 font-medium">
                                                    {t("Date") || "Date"}
                                                </th>
                                                <th className="text-center px-3 py-2 font-medium">
                                                    {t("WorkSpeed") || "Speed"}
                                                </th>
                                                <th className="text-center px-3 py-2 font-medium">
                                                    {t("WorkQuality") ||
                                                        "Quality"}
                                                </th>
                                                <th className="text-center px-3 py-2 font-medium">
                                                    {t("WorkLoad") || "Load"}
                                                </th>
                                                <th className="text-center px-3 py-2 font-medium">
                                                    {t("Overall") || "Overall"}
                                                </th>
                                                <th className="text-center px-3 py-2 font-medium">
                                                    {t("Members") || "Members"}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requestRows.map((r, i) => (
                                                <tr
                                                    key={i}
                                                    className="border-t border-gray-100"
                                                >
                                                    <td className="px-3 py-2 text-gray-500 text-xs">
                                                        {r.type}
                                                    </td>
                                                    <td className="px-4 py-2 text-gray-800">
                                                        {r.reference}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-600">
                                                        {r.date
                                                            ? formatDate(r.date)
                                                            : "—"}
                                                    </td>
                                                    <td className="text-center px-3 py-2 text-gray-600">
                                                        {r.speed}%
                                                    </td>
                                                    <td className="text-center px-3 py-2 text-gray-600">
                                                        {r.quality}%
                                                    </td>
                                                    <td className="text-center px-3 py-2 text-gray-600">
                                                        {r.load}%
                                                    </td>
                                                    <td className="text-center px-3 py-2 font-semibold text-[#9B0505]">
                                                        {r.overall}%
                                                    </td>
                                                    <td className="text-center px-3 py-2 text-gray-600">
                                                        {r.memberCount}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
